import { ProjectDot } from "@/components/projects/project-color";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/relative-time";
import { paginationSearchFields } from "@/lib/search";
import { cn, formatHours } from "@/lib/utils";
import { Link, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { Clock, Crown, LayoutGrid, List } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/projects` — listing page. Port of `src/app/(app)/projects/page.tsx`.
 *
 * Each project is a single click target: the whole card is a link
 * to the project detail. The card surfaces only the at-a-glance
 * signals — name, open task count, hours tracked in the last 7
 * days, and a relative "last activity" timestamp.
 *
 * URL state lives in `?q=` (search) and `?page=`; `loaderDeps`
 * plumbs them into the API call so the loader re-runs on change.
 */

const projectsSearchSchema = z.object({
  q: z.string().optional(),
  ...paginationSearchFields,
});

type ProjectsSearch = z.infer<typeof projectsSearchSchema>;

type ListedProject = {
  id: string;
  name: string;
  slug: string;
  /** GitLab group the project lives under. May be null while the
   * group hasn't been synced yet — handled with an "Ungrouped" bucket. */
  group: { id: string; path: string; name: string } | null;
  /** Customer is a billing concept; the server omits it for non-
   * accountants, so this field is optional rather than nullable. */
  customer?: { id: string; name: string };
  openTasksByPriority: { urgent: number; high: number; normal: number; low: number };
  trackedHours7d: number;
  lastActivityAt: string | null;
  /** The viewer's GitLab membership role in this project, or null. */
  myRoles: string[] | null;
  /** Whether the viewer is a manager of this project. */
  isManager: boolean;
};

/** Projects bucketed by their GitLab group — the listing's grouping
 * key. Group is `null` for the "Ungrouped" bucket. */
type ProjectGroup = {
  /** Synthetic key — group id, or the literal "__ungrouped__". */
  key: string;
  name: string;
  path: string | null;
  projects: ListedProject[];
};

function groupByGroup(projects: ListedProject[], ungrouped: string): ProjectGroup[] {
  const groups: ProjectGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const p of projects) {
    const key = p.group?.id ?? "__ungrouped__";
    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({
        key,
        name: p.group?.name ?? ungrouped,
        path: p.group?.path ?? null,
        projects: [],
      });
    }
    groups[idx]?.projects.push(p);
  }
  return groups;
}

/** Open-task priority buckets, in severity order, with a dot colour. */
const PRIORITY_DOTS = [
  { key: "urgent", dot: "bg-red-500" },
  { key: "high", dot: "bg-orange-500" },
  { key: "normal", dot: "bg-gray-400" },
  { key: "low", dot: "bg-blue-500" },
] as const;

type ProjectsResponse = {
  projects: ListedProject[];
  total: number;
  page: number;
  pageSize: number;
};

export const Route = createFileRoute("/_app/projects/")({
  validateSearch: projectsSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.q) params.set("q", deps.q);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    const qs = params.toString();
    return api.get<ProjectsResponse>(qs ? `/api/projects?${qs}` : "/api/projects");
  },
  component: ProjectsPage,
});

type ViewMode = "pills" | "table";

/** Remember the chosen view across sessions. localStorage keeps it
 * per-browser, which is what "i do dalších session" asks for — no
 * server round-trip needed and no cross-device coupling. */
function useProjectsView(): [ViewMode, (next: ViewMode) => void] {
  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "pills";
    return localStorage.getItem("projectsView") === "table" ? "table" : "pills";
  });
  const set = (next: ViewMode): void => {
    setView(next);
    try {
      localStorage.setItem("projectsView", next);
    } catch {
      // localStorage may be blocked (private mode / quota); ignore —
      // the in-memory state still works for this session.
    }
  };
  return [view, set];
}

function ProjectsPage() {
  const { projects, total, page, pageSize } = Route.useLoaderData();
  const search: ProjectsSearch = Route.useSearch();
  const layout = useLoaderData({ from: "/_app" });
  const { t } = useTranslation();
  const admin = layout.user.globalRole === "admin";
  const [view, setView] = useProjectsView();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("projList.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("projList.total", { count: total })}
          </p>
        </div>
        {admin && (
          <Button asChild>
            <Link to="/projects/new">{t("projList.add")}</Link>
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <SearchInput placeholder={t("projList.search")} />
        <ViewToggle value={view} onChange={setView} />
      </div>

      {projects.length === 0 ? (
        <div className="rounded-md border bg-card text-center text-muted-foreground py-12">
          {t("projList.empty")}
        </div>
      ) : view === "pills" ? (
        <div className="space-y-8">
          {groupByGroup(projects, t("projList.ungrouped")).map((group) => (
            <section key={group.key} className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-2 border-b pb-1.5">
                <h2
                  data-testid="projects-group-heading"
                  className="font-heading text-sm font-semibold uppercase tracking-wide"
                >
                  {group.name}
                </h2>
                {group.path && group.path !== group.name && (
                  <span className="font-mono text-xs text-muted-foreground/70">{group.path}</span>
                )}
                <span className="text-xs text-muted-foreground/70">
                  {t("projList.groupCount", { count: group.projects.length })}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {group.projects.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <ProjectsTable projects={projects} />
      )}

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="projects"
        basePath="/projects"
        searchParams={{ q: search.q, pageSize: search.pageSize }}
      />
    </div>
  );
}

/** Pills / table toggle. Pills is the default; the user's choice
 * persists across sessions (see `useProjectsView`). */
function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  const { t } = useTranslation();
  const base =
    "inline-flex items-center justify-center px-2.5 py-1 rounded text-xs transition-colors";
  return (
    <div
      aria-label={t("projList.viewAria")}
      className="inline-flex rounded-md border bg-card p-0.5"
    >
      <button
        type="button"
        aria-pressed={value === "pills"}
        aria-label={t("projList.pillView")}
        title={t("projList.pillView")}
        onClick={() => onChange("pills")}
        className={cn(base, value === "pills" ? "bg-accent" : "hover:bg-accent/50")}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-pressed={value === "table"}
        aria-label={t("projList.tableView")}
        title={t("projList.tableView")}
        onClick={() => onChange("table")}
        className={cn(base, value === "table" ? "bg-accent" : "hover:bg-accent/50")}
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Classic table view. Same data the cards surface, but in one
 * scannable list — group becomes a column instead of a section
 * header, customer joins only when the server included it. */
function ProjectsTable({ projects }: { projects: ListedProject[] }) {
  const { t } = useTranslation();
  const showCustomer = projects.some((p) => Boolean(p.customer));
  // No `sortAccessor`s on purpose: the listing is server-paginated
  // (the table only ever holds the current page), so client-side
  // sorting would misleadingly reorder just the visible slice.
  const columns: Array<DataTableColumn<ListedProject>> = [
    {
      key: "project",
      header: t("projList.colProject"),
      cell: (p) => (
        <Link
          to="/projects/$slug"
          params={{ slug: p.slug }}
          className="font-medium hover:underline inline-flex items-center gap-1.5"
        >
          <ProjectDot projectId={p.id} />
          {p.name}
          {p.isManager && (
            <Crown
              className="h-3.5 w-3.5 text-amber-500"
              fill="currentColor"
              aria-label={t("projList.youManage")}
            />
          )}
        </Link>
      ),
    },
    {
      key: "group",
      header: t("projList.colGroup"),
      cellClassName: "font-mono text-xs text-muted-foreground",
      cell: (p) => p.group?.path ?? "—",
    },
    {
      key: "customer",
      header: t("projList.colCustomer"),
      hidden: !showCustomer,
      cellClassName: "text-sm",
      cell: (p) => p.customer?.name ?? "—",
    },
    {
      key: "role",
      header: t("projList.colRole"),
      cell: (p) =>
        p.myRoles?.length ? (
          <Badge variant="secondary" className="text-[0.65rem] capitalize">
            {p.myRoles.join(", ")}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground/60">—</span>
        ),
    },
    {
      key: "open",
      header: t("projList.colOpen"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (p) =>
        p.openTasksByPriority.urgent +
        p.openTasksByPriority.high +
        p.openTasksByPriority.normal +
        p.openTasksByPriority.low,
    },
    {
      key: "tracked7d",
      header: t("projList.colTracked"),
      headClassName: "text-right",
      cellClassName: "text-right tabular-nums",
      cell: (p) => formatHours(p.trackedHours7d),
    },
    {
      key: "lastActivity",
      header: t("projList.colLastActivity"),
      cellClassName: "text-xs text-muted-foreground",
      cell: (p) => relativeTime(p.lastActivityAt),
    },
  ];
  return <DataTable columns={columns} rows={projects} rowKey={(p) => p.id} />;
}

/** A single project card — the whole card links to the detail. */
function ProjectCard({ project: p }: { project: ListedProject }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/projects/$slug"
      params={{ slug: p.slug }}
      className="group relative block rounded-lg border bg-card p-5 transition-colors hover:bg-accent/30"
    >
      {p.isManager && (
        <span
          className="absolute right-3 top-3 text-amber-500"
          title={t("projList.youManage")}
          aria-label={t("projList.youManage")}
        >
          <Crown className="h-4 w-4" fill="currentColor" />
        </span>
      )}

      <h2 className="font-heading text-xl font-bold leading-snug line-clamp-2 pr-6">
        <ProjectDot projectId={p.id} className="mr-1.5 align-middle" />
        {p.name}
      </h2>

      {/* Customer is shown only to accountants — for everyone else
          the server omits the field and the row stays clean. */}
      {p.customer && (
        <div
          data-testid="project-customer-name"
          className="text-xs text-muted-foreground/80 mt-0.5 truncate"
        >
          {p.customer.name}
        </div>
      )}

      <div className="mt-2 h-5">
        {p.myRoles?.length ? (
          <Badge variant="secondary" className="text-[0.65rem] capitalize">
            {p.myRoles.join(", ")}
          </Badge>
        ) : (
          <span className="text-[0.65rem] text-muted-foreground/60">{t("projList.notMember")}</span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold leading-none">
            {PRIORITY_DOTS.map((pr) => (
              <span
                key={pr.key}
                className="flex items-center gap-1"
                title={t(`projList.priority.${pr.key}`)}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${pr.dot}`} />
                {p.openTasksByPriority[pr.key]}
              </span>
            ))}
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">{t("projList.openTasks")}</div>
        </div>
        <div>
          <div className="text-sm font-semibold leading-none">{formatHours(p.trackedHours7d)}</div>
          <div className="mt-1.5 text-xs text-muted-foreground">{t("projList.tracked7d")}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-1.5 border-t pt-3 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        <span>{relativeTime(p.lastActivityAt)}</span>
      </div>
    </Link>
  );
}
